import { ConnectorMode, FontFamily, FontWeight, StrokeStyle } from '@blocksuite/affine-model';
import type { MindmapRoot } from '@blocksuite/blocks';

import { ShapeType } from '../elements/shape/consts.js';
import type { MindmapNode, NodeDetail } from './mindmap.js';
import { LayoutType, MindmapElementModel } from './mindmap.js';
import type { ConnectorStyle, NodeStyle } from './utils/mindmap/style.js';
import { MindmapStyleGetter } from './utils/mindmap/style.js';

export interface BubbleNode {
  label: string;
  value?: number;
  children?: BubbleNode[];
  image?: {
    url?: string;
    sourceId?: string;
    svg?: string;
  };
  nodeType?: 'text' | 'image' | 'icon';
  tags?: Array<{ name: string; color?: string }>;
}

const BUBBLE_CONFIG = {
  themes: [
    { strokeColor: '--affine-palette-line-purple', shadowColor: 'rgba(155, 81, 224, 0.25)' },
    { strokeColor: '--affine-palette-line-magenta', shadowColor: 'rgba(245, 92, 222, 0.25)' },
    { strokeColor: '--affine-palette-line-orange', shadowColor: 'rgba(255, 165, 0, 0.25)' },
    { strokeColor: '--affine-palette-line-yellow', shadowColor: 'rgba(255, 220, 0, 0.25)' },
    { strokeColor: '--affine-palette-line-green', shadowColor: 'rgba(76, 175, 80, 0.25)' },
    { strokeColor: '#7ae2d5', shadowColor: 'rgba(122, 226, 213, 0.15)' },
  ],
  connector: {
    strokeWidth: 1.5,
    mode: ConnectorMode.Curve,
  },
  node: {
    base: {
      fillColor: '--affine-background-primary-color',
      fontFamily: FontFamily.Poppins,
      color: '--affine-text-primary-color',
      radius: 0,
      strokeWidth: 2,
    },
    root: {
      fontSize: 20,
      fontWeight: FontWeight.Bold,
      padding: [12, 24] as [number, number],
      filled: false,
      fillColor: 'transparent',
      strokeColor: 'transparent',
      strokeWidth: 0,
    },
    child: {
      fontSize: 16,
      fontWeight: FontWeight.SemiBold,
      padding: [8, 16] as [number, number],
      filled: true,
      shadow: { blur: 12, offsetX: 0, offsetY: 3 },
    },
    icon: {
      fontSizeScale: 0.6,
      fontWeight: FontWeight.Medium,
    },
  },
  sizing: {
    baseScale: 0.8,
    image: { maxSize: 25, scale: 0.5 },
    icon: { minWidth: 0.95, minHeight: 1.1, minSize: 45 },
    tag: { baseExtra: 1.2, perTag: 0.08, minSize: 50 },
  },
  imageSpacing: 8,
} as const;

class BubbleStyleGetter extends MindmapStyleGetter {
  readonly root: NodeStyle = {
    ...BUBBLE_CONFIG.node.base,
    ...BUBBLE_CONFIG.node.root,
  };

  getNodeStyle(node: MindmapNode, path: number[]): { connector: ConnectorStyle; node: NodeStyle } {
    const rankIdx = path[1] ?? 0;
    const theme = BUBBLE_CONFIG.themes[rankIdx % BUBBLE_CONFIG.themes.length];
    const isImageNode = (node.detail as NodeDetail)?.nodeType === 'image';

    return {
      connector: {
        strokeStyle: StrokeStyle.Solid,
        stroke: isImageNode ? 'transparent' : theme.strokeColor,
        strokeWidth: isImageNode ? 0 : BUBBLE_CONFIG.connector.strokeWidth,
        mode: BUBBLE_CONFIG.connector.mode,
      },
      node: {
        ...BUBBLE_CONFIG.node.base,
        ...BUBBLE_CONFIG.node.child,
        strokeColor: theme.strokeColor,
        shadow: {
          ...BUBBLE_CONFIG.node.child.shadow,
          color: theme.shadowColor,
        },
      },
    };
  }
}

const bubbleStyleGetter = new BubbleStyleGetter();

export class BubbleCollectionElementModel extends MindmapElementModel {
  static override propsToY(props: Record<string, unknown>) {
    if (props.children && typeof props.children === 'object' && 'label' in props.children) {
      const convertNode = (
        node: BubbleNode
      ): { text: string; children?: any[]; image?: NodeDetail['image']; nodeType?: NodeDetail['nodeType']; tags?: NodeDetail['tags'] } => {
        const result: { text: string; children?: any[]; image?: NodeDetail['image']; nodeType?: NodeDetail['nodeType']; tags?: NodeDetail['tags'] } = {
          text: node.label || '',
          children: node.children?.map(convertNode),
        };

        if (node.image) {
          result.image = {
            url: node.image.url,
            svg: node.image.svg
          };
          result.nodeType = node.nodeType;
        }

        if (node.tags) {
          result.tags = node.tags;
        }

        return result;
      };
      props.children = convertNode(props.children as BubbleNode);
    }
    return MindmapElementModel.propsToY(props);
  }

  override get layoutType(): LayoutType {
    return LayoutType.BALANCE;
  }

  override get styleGetter() {
    return bubbleStyleGetter;
  }

  override applyStyle(_fitContent = true): void {
    super.applyStyle(true);

    this.surface.doc.transact(() => {
      this.traverse((node, parent) => {
        const el = node.element as any;
        const nodeDetail = node.detail as NodeDetail;
        const isRoot = !parent;

        el.shapeType = ShapeType.Ellipse;

        if (nodeDetail?.tags?.length) {
          el.tags = nodeDetail.tags;
        }

        const w = Number(el.w ?? 0);
        const h = Number(el.h ?? 0);
        if (w && h) {
          const side = this._calculateNodeSize(nodeDetail, w, h);
          const newX = Number(el.x) - (side - w) / 2;
          const newY = Number(el.y) - (side - h) / 2;
          el.xywh = `[${Math.round(newX)},${Math.round(newY)},${Math.round(side)},${Math.round(side)}]`;
        }

        if (isRoot) {
          Object.assign(el, {
            filled: BUBBLE_CONFIG.node.root.filled,
            strokeWidth: BUBBLE_CONFIG.node.root.strokeWidth,
            fillColor: BUBBLE_CONFIG.node.root.fillColor,
            strokeColor: BUBBLE_CONFIG.node.root.strokeColor,
            fontFamily: BUBBLE_CONFIG.node.base.fontFamily,
            fontWeight: BUBBLE_CONFIG.node.root.fontWeight,
            color: BUBBLE_CONFIG.node.base.color,
          });
        } else {
          const rankIdx = parent.children.indexOf(node);
          const theme = BUBBLE_CONFIG.themes[rankIdx % BUBBLE_CONFIG.themes.length];

          Object.assign(el, {
            filled: BUBBLE_CONFIG.node.child.filled,
            fillColor: BUBBLE_CONFIG.node.base.fillColor,
            strokeColor: theme.strokeColor,
            strokeWidth: BUBBLE_CONFIG.node.base.strokeWidth,
            shadow: {
              ...BUBBLE_CONFIG.node.child.shadow,
              color: theme.shadowColor,
            },
            fontFamily: BUBBLE_CONFIG.node.base.fontFamily,
            fontWeight: BUBBLE_CONFIG.node.child.fontWeight,
            color: BUBBLE_CONFIG.node.base.color,
          });

          if (nodeDetail?.nodeType === 'icon') {
            el.fontSize = Math.max(8, Number(el.fontSize || 14) * BUBBLE_CONFIG.node.icon.fontSizeScale);
            el.fontWeight = BUBBLE_CONFIG.node.icon.fontWeight;
          }
        }
      });
    });
  }

  private _calculateNodeSize(nodeDetail: NodeDetail, w: number, h: number): number {
    const hasImage = Boolean(nodeDetail?.image?.url || nodeDetail?.image?.svg);
    const isIcon = nodeDetail?.nodeType === 'icon';
    const hasTags = Boolean(nodeDetail?.tags?.length);

    let side = Math.max(w, h) * BUBBLE_CONFIG.sizing.baseScale;

    if (hasImage && !isIcon) {
      side = Math.min(side * BUBBLE_CONFIG.sizing.image.scale, BUBBLE_CONFIG.sizing.image.maxSize);
    } else if (isIcon) {
      side = Math.max(
        w * BUBBLE_CONFIG.sizing.icon.minWidth,
        h * BUBBLE_CONFIG.sizing.icon.minHeight,
        BUBBLE_CONFIG.sizing.icon.minSize
      );
    } else if (hasTags && nodeDetail.tags) {
      const tagCount = Math.min(nodeDetail.tags.length, 3);
      const extraSpace = BUBBLE_CONFIG.sizing.tag.baseExtra + (tagCount * BUBBLE_CONFIG.sizing.tag.perTag);
      side = Math.max(w * 0.95, h * extraSpace, BUBBLE_CONFIG.sizing.tag.minSize);
    }

    return side;
  }

  override get type() {
    return 'collection-bubble';
  }

  override detach(_subtree: string | MindmapNode) {
    return undefined;
  }

  override layout(
    tree: MindmapNode | MindmapRoot = this.tree,
    applyStyle = true,
    layoutType?: LayoutType
  ) {
    if (!tree || !tree.element) return;

    if (applyStyle) {
      this.applyStyle(true);
    }

    super.layout(tree, false, layoutType);

    this.surface.doc.transact(() => {
      this._repositionImageNodes(tree);
    });
  }

  private _repositionImageNodes(node: MindmapNode): void {
    if (!node.children?.length) return;

    node.children.forEach(child => {
      const childDetail = child.detail as NodeDetail;
      const isIconNode = childDetail?.nodeType === 'icon';
      const hasImage = Boolean(childDetail?.image?.url || childDetail?.image?.svg);

      if (hasImage && !isIconNode) {
        const parentBound = node.element.elementBound;
        const childBound = child.element.elementBound;

        const currentCx = childBound.x + childBound.w / 2;
        const currentCy = childBound.y + childBound.h / 2;
        const parentCx = parentBound.x + parentBound.w / 2;
        const parentCy = parentBound.y + parentBound.h / 2;

        const angle = Math.atan2(currentCy - parentCy, currentCx - parentCx);

        const parentRadius = Math.max(parentBound.w, parentBound.h) / 2;
        const childRadius = Math.max(childBound.w, childBound.h) / 2;
        const distance = parentRadius + childRadius + BUBBLE_CONFIG.imageSpacing;

        const newCx = parentCx + Math.cos(angle) * distance;
        const newCy = parentCy + Math.sin(angle) * distance;

        const newX = newCx - childBound.w / 2;
        const newY = newCy - childBound.h / 2;

        child.element.xywh = `[${Math.round(newX)},${Math.round(newY)},${childBound.w},${childBound.h}]`;
      }

      this._repositionImageNodes(child);
    });
  }
}
